/**
 * TwinCircles — "Who's talking" grid for a group room (Step 11b, SPEC section 1).
 *
 * Each twin is a straight portrait card (the empty-conversation fan's card,
 * upright and smaller), five to a row so a ten-twin room fits in two rows.
 *
 * One button per twin, 5 columns. A tap, Enter or Space toggles
 * Sitting out <-> In; a picked twin (asked next, from the composer's @ picker)
 * goes to Sitting out (the owner of the state is useTellerPicks). States are
 * drawn, never colour alone: off is dimmed, grayscale with a dashed outline;
 * picked adds an accent ring and a mic badge.
 * The group is one tab stop with a roving tabindex.
 *
 * The profile card is the caller's (`renderProfile` wraps a trigger). On desktop
 * the whole circle is the trigger, a hover card that never opens on click, so a
 * click still cycles. Below md a tap on the circle cycles and the name under it
 * becomes its own button that opens the profile.
 */

import { ReactElement, ReactNode, useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Mic } from '@digital-twins/design-system/icons'

import { UserAvatar } from '@/components/ui/UserAvatar'
import { usePersonaThumbFallback } from '@/components/shared/PersonaThumbImg'
import { useIsNarrowViewport } from '@/hooks/useIsNarrowViewport'
import { cn } from '@/lib/utils'
import { getUserInitials } from '@/lib/utils/avatar'
import { TwinCircleState } from '@/hooks/useTellerPicks'

import { ChatRoomParticipantDto } from '@digital-twins/shared-types'

const COLUMNS = 5

interface TwinCirclesProps {
  participants: Array<ChatRoomParticipantDto>
  states: Record<string, TwinCircleState>
  onCycle: (personaId: string) => void
  isDisabled?: boolean
  /** Wraps a trigger with the twin's profile card; without it no card is shown. */
  renderProfile?: (participant: ChatRoomParticipantDto, trigger: ReactElement) => ReactNode
}

function getFirstName(displayName: string): string {
  return displayName.trim().split(/\s+/)[0] ?? displayName
}

export function TwinCircles(props: TwinCirclesProps) {
  const { participants, states, onCycle, isDisabled = false, renderProfile } = props

  const { t } = useTranslation('app')
  const [focusIndex, setFocusIndex] = useState(0)
  const [announcedPersonaId, setAnnouncedPersonaId] = useState<string | null>(null)
  const buttonRefs = useRef<Array<HTMLButtonElement | null>>([])
  const hintId = useId()
  const isNarrow = useIsNarrowViewport()

  const stateLabel = (state: TwinCircleState | undefined) =>
    state === 'picked'
      ? t('roomPane.statePicked')
      : state === 'bidder'
        ? t('roomPane.stateIn')
        : t('roomPane.stateOff')

  const moveFocus = (index: number) => {
    const bounded = Math.max(0, Math.min(participants.length - 1, index))
    setFocusIndex(bounded)
    buttonRefs.current[bounded]?.focus()
  }

  const onActivate = (personaId: string, index: number) => {
    if (isDisabled) {
      return
    }
    setFocusIndex(index)
    setAnnouncedPersonaId(personaId)
    onCycle(personaId)
  }

  const onKeyDown = (event: React.KeyboardEvent, index: number) => {
    const moves: Record<string, number> = {
      ArrowRight: index + 1,
      ArrowLeft: index - 1,
      ArrowDown: index + COLUMNS,
      ArrowUp: index - COLUMNS,
      Home: 0,
      End: participants.length - 1,
    }
    if (event.key in moves) {
      event.preventDefault()
      moveFocus(moves[event.key])
    }
  }

  const announced = participants.find((p) => p.personaId === announcedPersonaId)
  const announcement = announced
    ? t('roomPane.circleLabel', {
        name: announced.displayName,
        state: stateLabel(states[announced.personaId]),
      })
    : ''

  return (
    <>
      <div
        role="group"
        aria-label={t('roomPane.whoIsTalking')}
        data-testid="twin-circles"
        className="grid grid-cols-5 gap-x-2 gap-y-3"
      >
        {participants.map((participant, index) => (
          <TwinCircle
            key={participant.personaId}
            participant={participant}
            index={index}
            state={states[participant.personaId] ?? 'off'}
            stateLabel={stateLabel(states[participant.personaId])}
            isDisabled={isDisabled}
            isTabStop={index === Math.min(focusIndex, participants.length - 1)}
            hintId={hintId}
            buttonRef={(el) => {
              buttonRefs.current[index] = el
            }}
            onActivate={() => onActivate(participant.personaId, index)}
            onKeyDown={(event) => onKeyDown(event, index)}
            isNarrow={isNarrow}
            renderProfile={renderProfile}
          />
        ))}
      </div>
      <span id={hintId} className="sr-only">
        {t('roomPane.circleHint')}
      </span>
      <span className="sr-only" aria-live="polite" data-testid="twin-circles-live">
        {announcement}
      </span>
    </>
  )
}

interface TwinCircleProps {
  participant: ChatRoomParticipantDto
  index: number
  state: TwinCircleState
  stateLabel: string
  isDisabled: boolean
  isTabStop: boolean
  hintId: string
  buttonRef: (el: HTMLButtonElement | null) => void
  onActivate: () => void
  onKeyDown: (event: React.KeyboardEvent) => void
  isNarrow: boolean
  renderProfile?: (participant: ChatRoomParticipantDto, trigger: ReactElement) => ReactNode
}

function TwinCircle(props: TwinCircleProps) {
  const {
    participant,
    index,
    state,
    stateLabel,
    isDisabled,
    isTabStop,
    hintId,
    buttonRef,
    onActivate,
    onKeyDown,
    isNarrow,
    renderProfile,
  } = props

  const { t } = useTranslation('app')
  const { displayUrl, handleImageError } = usePersonaThumbFallback(participant.imageUrl, 128)
  const [hasImageFailed, setHasImageFailed] = useState(false)

  const isOff = state === 'off'
  const isPicked = state === 'picked'
  const initials = getUserInitials(participant.displayName)
  const label = t('roomPane.circleLabel', { name: participant.displayName, state: stateLabel })

  const badge = isPicked ? (
    <span
      aria-hidden="true"
      className="absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-twin-chat-accent text-twin-chat-bg ring-2 ring-muted"
    >
      <Mic className="h-2.5 w-2.5" strokeWidth={2.5} />
    </span>
  ) : null

  const avatar = (
    <span
      className={cn(
        'relative block w-full rounded-xl transition-[box-shadow,opacity] duration-200 motion-reduce:transition-none',
        !isDisabled && 'group-hover:ring-4 group-hover:ring-twin-chat-fg/[0.12]',
        isOff && 'opacity-45 outline-dashed outline-1 outline-offset-1 outline-twin-chat-muted',
        isPicked &&
          'ring-2 ring-twin-chat-accent ring-offset-2 ring-offset-muted group-hover:ring-2 group-hover:ring-twin-chat-accent',
      )}
    >
      <span
        className={cn(
          'block aspect-[4/5] w-full overflow-hidden rounded-xl border-2 border-background bg-twin-chat-bubble/30 shadow-md',
          isOff && 'grayscale',
        )}
      >
        {displayUrl && !hasImageFailed ? (
          <img
            src={displayUrl}
            alt=""
            className="h-full w-full object-cover object-top"
            onError={() => handleImageError(() => setHasImageFailed(true))}
          />
        ) : (
          <span className="flex h-full w-full items-center justify-center">
            <UserAvatar initials={initials} name={participant.displayName} size="sm" />
          </span>
        )}
      </span>
      {badge}
    </span>
  )

  const nameClassName = cn(
    'w-full truncate text-center text-2xs leading-tight',
    isOff ? 'text-twin-chat-muted' : 'text-twin-chat-fg',
    isPicked && 'font-semibold',
  )
  const firstName = getFirstName(participant.displayName)
  // Below md the name leaves the circle: it is the profile's own button there.
  const isNameSeparate = isNarrow && Boolean(renderProfile)

  const circle = (
    <button
      type="button"
      ref={buttonRef}
      data-testid={`twin-circle-${index}`}
      data-state-name={state}
      aria-pressed={!isOff}
      aria-label={label}
      aria-describedby={hintId}
      tabIndex={isTabStop ? 0 : -1}
      disabled={isDisabled}
      onClick={onActivate}
      onKeyDown={onKeyDown}
      className={cn(
        'group flex min-h-[44px] min-w-0 flex-col items-center gap-1 rounded-lg px-0.5 py-1 transition-opacity',
        'focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-ring',
        isNameSeparate && 'min-w-[44px] justify-center',
        isDisabled && 'cursor-not-allowed opacity-50',
      )}
    >
      {avatar}
      {isNameSeparate ? null : <span className={nameClassName}>{firstName}</span>}
    </button>
  )

  if (!renderProfile) {
    return circle
  }

  if (!isNameSeparate) {
    return <>{renderProfile(participant, circle)}</>
  }

  const nameButton = (
    <button
      type="button"
      data-testid={`twin-circle-name-${index}`}
      aria-label={t('roomPane.viewProfile', { name: participant.displayName })}
      className={cn(
        nameClassName,
        'min-h-[24px] rounded-md px-0.5 underline-offset-2 hover:underline',
        'focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring',
      )}
    >
      {firstName}
    </button>
  )

  return (
    <div className="flex min-w-0 flex-col items-center">
      {circle}
      {renderProfile(participant, nameButton)}
    </div>
  )
}
